import React, { useEffect, useState } from 'react';
import { X, ShoppingBag, Loader2, AlertTriangle, CheckCircle, Coffee, Briefcase, Sparkles } from 'lucide-react';
import axios from 'axios';
import { getApiBookingRouteId, getUserFacingBookingId } from '../utils/userFacingBookingId';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sprint C — Riya Post-Booking SSR Modal.
 *
 * Two-step flow:
 *   1. GET /api/riya/ssr/{booking_id} — list available SSR catalogue
 *   2. POST /api/riya/ssr/add — submit selection + wallet debit
 *
 * Currently wallet-only (agent). Gateway path will return 501.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   booking: { id|booking_id, riya_pnr, airline_pnr, passengers: [...] }
 */
const STEPS = { LOADING: 'loading', PICKING: 'picking', SUBMITTING: 'submitting', DONE: 'done', ERROR: 'error' };

export default function RiyaSSRModal({ open, onClose, booking = {} }) {
  const [step, setStep] = useState(STEPS.LOADING);
  const [catalogue, setCatalogue] = useState({ meals: [], baggage: [], other: [] });
  const [selected, setSelected] = useState({ meals: {}, baggage: {}, other: {} });
  const [errMsg, setErrMsg] = useState('');
  const [supplierDetail, setSupplierDetail] = useState('');
  const [result, setResult] = useState(null);

  const passengers = booking.passengers || [];
  const apiBookingId = getApiBookingRouteId(booking) || booking.id || booking.booking_id;
  const displayBookingRef = getUserFacingBookingId(booking);

  useEffect(() => {
    if (!open || !apiBookingId) return;
    const controller = new AbortController();
    setStep(STEPS.LOADING);
    setSelected({ meals: {}, baggage: {}, other: {} });
    setErrMsg('');
    setResult(null);
    (async () => {
      try {
        const resp = await axios.get(`${API}/riya/ssr/${apiBookingId}`, {
          withCredentials: true,
          signal: controller.signal,
          validateStatus: () => true,
        });
        const data = resp.data;
        if (resp.status < 200 || resp.status >= 300) {
          const rawMsg = (data && (typeof data.detail === 'string' ? data.detail : (data.detail?.message || data.message))) || `SSR not available (${resp.status})`;
          const supplierDown = resp.status === 502
            || /Proxy Error|Login|TravelAPI\.svc|ReadTimeout|Bad Gateway/i.test(String(rawMsg));
          const looksLikeSupplierBusiness = /RiyaPNR|FlightId|Trackid|tag is null|tag is empty|not in correct format|penality|Reschedule|customer care/i.test(String(rawMsg));
          const friendly = supplierDown
            ? 'Riya supplier is temporarily unreachable. Please retry in a few minutes.'
            : looksLikeSupplierBusiness
            ? 'Extras are not available for this booking right now. Please contact support if you need to add baggage or meals.'
            : rawMsg;
          const err = new Error(friendly);
          err.supplierDetail = String(rawMsg);
          throw err;
        }
        setCatalogue((data && data.ssr) || { meals: [], baggage: [], other: [] });
        setStep(STEPS.PICKING);
      } catch (e) {
        if (e.name === 'AbortError' || e.name === 'CanceledError') return;
        setErrMsg(e.message || 'Network error');
        setSupplierDetail(e.supplierDetail || '');
        setStep(STEPS.ERROR);
      }
    })();
    return () => controller.abort();
  }, [open, apiBookingId]);

  if (!open) return null;

  // Toggle selection
  const toggle = (kind, ssrKey, paxIdx, item) => {
    setSelected((prev) => {
      const next = { ...prev, [kind]: { ...prev[kind] } };
      const key = `${ssrKey}_${paxIdx}`;
      if (next[kind][key]) {
        delete next[kind][key];
      } else {
        next[kind][key] = {
          paxIdx, item,
          PaxRefId: paxIdx + 1,
          SegmentNo: Number(item.SegRef || 0) + 1,
          ...(kind === 'meals' ? { MealId: item.Id } : kind === 'baggage' ? { BaggId: item.Id } : { Id: item.Id }),
        };
      }
      return next;
    });
  };

  const totalAmount = ['meals', 'baggage', 'other'].reduce((sum, k) => {
    return sum + Object.values(selected[k]).reduce((s, sel) => s + Number(sel.item.Amount || 0), 0);
  }, 0);

  const totalCount = ['meals', 'baggage', 'other'].reduce((c, k) => c + Object.keys(selected[k]).length, 0);

  const submit = async () => {
    if (totalCount === 0) {
      setErrMsg('Please select at least one item');
      return;
    }
    setStep(STEPS.SUBMITTING);
    setErrMsg('');
    try {
      const flatten = (kind) => Object.values(selected[kind]).map((s) => {
        const out = { PaxRefId: s.PaxRefId, SegmentNo: s.SegmentNo };
        if (kind === 'meals') out.MealId = s.MealId;
        else if (kind === 'baggage') out.BaggId = s.BaggId;
        else out.Id = s.Id;
        return out;
      });
      const resp = await axios.post(`${API}/riya/ssr/add`, {
        booking_id: apiBookingId,
        meals: flatten('meals'),
        baggage: flatten('baggage'),
        other: flatten('other'),
        amount: totalAmount,
        payment_method: 'wallet',
      }, { withCredentials: true, validateStatus: () => true });
      const data = resp.data;
      if (resp.status < 200 || resp.status >= 300) {
        const rawMsg = (data && (typeof data.detail === 'string' ? data.detail : (data.detail?.message || data.message))) || `Failed to add SSR (${resp.status})`;
        const supplierDown = resp.status === 502
          || /Proxy Error|Login|TravelAPI\.svc|ReadTimeout|Bad Gateway/i.test(String(rawMsg));
        const looksLikeSupplierBusiness = /RiyaPNR|FlightId|Trackid|tag is null|tag is empty|not in correct format/i.test(String(rawMsg));
        const friendly = supplierDown
          ? 'Riya supplier is temporarily unreachable. Please retry in a few minutes.'
          : looksLikeSupplierBusiness
          ? 'Extras could not be added at this time. Please contact support to assist with this booking.'
          : rawMsg;
        const err = new Error(friendly);
        err.supplierDetail = String(rawMsg);
        throw err;
      }
      setResult(data);
      setStep(STEPS.DONE);
    } catch (e) {
      setErrMsg(e.message || 'Network error');
      setSupplierDetail(e.supplierDetail || '');
      setStep(STEPS.ERROR);
    }
  };

  const renderCatalogueSection = (kind, items, Icon) => {
    if (!items.length) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-emerald-600" />
          <h4 className="text-sm font-bold text-gray-800 capitalize">{kind === 'other' ? 'Other Services' : kind}</h4>
        </div>
        {items.map((item) => (
          <div key={item.Id} className="border border-gray-200 rounded-lg p-3" data-testid={`riya-ssr-${kind}-${item.Id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-800">{item.Description}</span>
              <span className="text-sm font-bold text-emerald-700">₹{Number(item.Amount).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {passengers.length === 0 && (
                <button
                  type="button"
                  onClick={() => toggle(kind, item.Id, 0, item)}
                  className={`text-xs px-2 py-1 rounded border ${selected[kind][`${item.Id}_0`] ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'border-gray-300 text-gray-600'}`}
                >
                  Add
                </button>
              )}
              {passengers.map((p, idx) => {
                const fullName = `${p.first_name || p.fN || ''} ${p.last_name || p.lN || ''}`.trim() || `Pax ${idx + 1}`;
                const isSelected = !!selected[kind][`${item.Id}_${idx}`];
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => toggle(kind, item.Id, idx, item)}
                    className={`text-xs px-2 py-1 rounded border ${isSelected ? 'bg-emerald-100 border-emerald-400 text-emerald-700' : 'border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    data-testid={`riya-ssr-toggle-${kind}-${item.Id}-${idx}`}
                  >
                    {fullName}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4" data-testid="riya-ssr-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-bold text-gray-800">
              Add Extras
              <span className="ml-2 text-xs font-medium text-gray-500">(Riya)</span>
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="riya-ssr-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {step === STEPS.LOADING && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">Loading available extras…</p>
            </div>
          )}

          {step === STEPS.PICKING && (
            <>
              <div className="text-sm text-gray-700 space-y-1">
                <p>Booking: <span className="font-mono">{displayBookingRef}</span></p>
                <p>PNR: <span className="font-mono">{booking.riya_pnr || booking.pnr || '—'}</span></p>
              </div>
              {renderCatalogueSection('meals', catalogue.meals, Coffee)}
              {renderCatalogueSection('baggage', catalogue.baggage, Briefcase)}
              {renderCatalogueSection('other', catalogue.other, Sparkles)}

              {(catalogue.meals.length + catalogue.baggage.length + catalogue.other.length === 0) && (
                <p className="text-sm text-gray-500 text-center py-6">No extras available for this booking.</p>
              )}

              {errMsg && <p className="text-xs text-red-600">{errMsg}</p>}

              <div className="border-t border-gray-200 pt-3 sticky bottom-0 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">{totalCount} item{totalCount !== 1 && 's'} selected</span>
                  <span className="text-lg font-bold text-emerald-700" data-testid="riya-ssr-total">
                    ₹{totalAmount.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={totalCount === 0}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="riya-ssr-submit-btn"
                  >
                    Pay & Add (Wallet)
                  </button>
                </div>
              </div>
            </>
          )}

          {step === STEPS.SUBMITTING && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-3" />
              <p className="text-sm font-semibold text-gray-800">Adding extras with Riya…</p>
            </div>
          )}

          {step === STEPS.DONE && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-base font-semibold text-gray-800">Extras added successfully</p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3 text-left text-sm space-y-1">
                <p><span className="text-gray-600">Confirmation:</span> <span className="font-mono">{result?.confirmation_number || '—'}</span></p>
                <p><span className="text-gray-600">Charged:</span> <strong className="text-emerald-700">₹{Number(result?.amount_charged || 0).toLocaleString('en-IN')}</strong></p>
                <p><span className="text-gray-600">Wallet txn:</span> <span className="font-mono">{result?.wallet_transaction_id || '—'}</span></p>
              </div>
              <button onClick={onClose} className="mt-4 w-full px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium" data-testid="riya-ssr-done-btn">
                Close
              </button>
            </div>
          )}

          {step === STEPS.ERROR && (
            <div className="text-center py-4" data-testid="riya-ssr-error-state">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-base font-semibold text-gray-800">Could not add extras</p>
              <p className="text-sm text-gray-600 mt-2" data-testid="riya-ssr-error-msg">{errMsg}</p>
              {supplierDetail && supplierDetail !== errMsg && (
                <details className="text-left mt-3 text-xs">
                  <summary className="text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                    Show technical detail (for support)
                  </summary>
                  <p className="text-gray-500 mt-1 font-mono break-words" data-testid="riya-ssr-supplier-detail">{supplierDetail}</p>
                </details>
              )}
              <div className="flex gap-2 mt-4">
                <a
                  href="/agent/support-tickets"
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium text-sm"
                  data-testid="riya-ssr-contact-support-btn"
                >
                  Contact support
                </a>
                <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium text-sm" data-testid="riya-ssr-error-close-btn">
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
